import React, { useCallback, useEffect } from 'react'
import {
  OneEmptyState,
  ResourceHeader,
  Spinner,
  Stack,
} from '@factorialco/f0-react/dist/experimental'
import { F0Dialog } from '@factorialco/f0-react'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  TicketDetailDocument,
  UpdateTicketDocument,
  TicketsConnectionDocument,
  GenerateTicketingAiSuggestionsDocument,
} from 'generated/resources/graphql'
import {
  trackOpenTicketDetail,
  trackUpdateTicketStatus,
  trackUpdateTicketPriority,
  trackUpdateTicketAssigneeTeam,
  trackUpdateTicketAssigneeMember,
} from 'modules/ticketing/lib/tracking'
import { TicketAttachments } from 'modules/ticketing/components/TicketDetailPanel/TicketAttachments'
import { TicketComments } from 'modules/ticketing/components/TicketDetailPanel/TicketComments'
import { TicketDetailList } from 'modules/ticketing/components/TicketDetailPanel/TicketDetailList'
import { TicketAiInsights } from 'modules/ticketing/components/TicketDetailPanel/TicketAiInsights'
import { useTicketingPolicies } from 'modules/ticketing/hooks/useTicketingPolicies'

type Data = {
  ticketId: string
}

export const TicketDetailActivity: ActivityComponent<Data> = ({ ticketId }) => {
  const { open, closeActivity } = useActivity()

  const { aiEnabled } = useTicketingPolicies()

  useEffect(() => {
    if (!ticketId) {
      closeActivity()
    }
  }, [ticketId, closeActivity])

  const {
    data,
    loading,
    error,
    refetch: refetchTicket,
  } = useQuery(TicketDetailDocument, {
    variables: { id: ticketId },
    fetchPolicy: 'network-only',
    skip: !ticketId,
  })

  const [performUpdate] = useMutation(UpdateTicketDocument, {
    extractorFn: (result) => result.ticketingMutations.updateTicketingTicket,
    refetchQueries: [TicketsConnectionDocument],
  })

  const ticket = data?.ticketing?.ticket ?? null
  useEffect(() => {
    if (ticket) {
      trackOpenTicketDetail({
        status: ticket.status,
        priority: ticket.priority,
        has_ai_summary: Boolean(ticket.aiSummary),
        has_ai_resolution_proposal: Boolean(ticket.aiResolutionProposal),
      })
    }
  }, [ticket?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldUpdate = useCallback(
    async (variables: Record<string, unknown>) => {
      if (!ticket) return

      const result = await performUpdate({
        variables: {
          id: ticket.id,
          ...variables,
        },
      })

      const handled = handleResult(result, () => {})
      if (handled.success && handled.data?.ticket != null) {
        refetchTicket()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ticketing.inbox.detail.update_error'),
        })
      }
    },
    [ticket, performUpdate, refetchTicket]
  )

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      if (ticket) {
        trackUpdateTicketStatus({ previous_status: ticket.status, new_status: newStatus })
      }
      handleFieldUpdate({ status: newStatus })
    },
    [handleFieldUpdate, ticket]
  )

  const handlePriorityChange = useCallback(
    (newPriority: string) => {
      if (ticket) {
        trackUpdateTicketPriority({
          previous_priority: ticket.priority,
          new_priority: newPriority,
        })
      }
      handleFieldUpdate({ priority: newPriority })
    },
    [handleFieldUpdate, ticket]
  )

  const handleAssigneeChange = useCallback(
    (fields: { assigneeTeamId?: string; assigneeMemberId?: string | null }) => {
      if (fields.assigneeTeamId !== undefined) {
        trackUpdateTicketAssigneeTeam({
          is_reassignment: ticket?.assigneeTeamId != null,
        })
      }
      if (fields.assigneeMemberId !== undefined) {
        trackUpdateTicketAssigneeMember({
          is_reassignment: ticket?.assigneeMemberId != null,
        })
      }
      handleFieldUpdate(fields)
    },
    [handleFieldUpdate, ticket]
  )

  const [performGenerateAiSuggestions, { loading: generatingAiSuggestions }] = useMutation(
    GenerateTicketingAiSuggestionsDocument,
    {
      extractorFn: (result) => result.ticketingMutations.generateTicketingTicketAiSuggestion,
    }
  )

  const handleGenerateAiSuggestions = useCallback(async () => {
    if (!ticket) return
    const result = await performGenerateAiSuggestions({
      variables: { ticketId: ticket.id },
    })
    const handled = handleResult(result, () => {})
    if (handled.success) {
      refetchTicket()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ticketing.inbox.ai.generation_error'),
      })
    }
  }, [ticket, performGenerateAiSuggestions, refetchTicket])

  const notFound = !loading && ticket == null && error == null && Boolean(ticketId)
  const showError = Boolean(error)

  const renderContent = () => {
    if (showError) {
      return (
        <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-detail-panel">
          <OneEmptyState
            title={i18n.t('ticketing.inbox.error_state_title')}
            description={i18n.t('ticketing.inbox.error_state_description')}
            variant="critical"
          />
        </Stack>
      )
    }

    if (notFound) {
      return (
        <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-detail-panel">
          <OneEmptyState
            title={i18n.t('ticketing.inbox.detail.not_found')}
            description={i18n.t('ticketing.inbox.detail.not_found_description')}
            variant="critical"
          />
        </Stack>
      )
    }

    if (loading && !ticket) {
      return (
        <Stack
          paddingX="p-4"
          paddingY="p-4"
          gap="4"
          alignItems="center"
          data-testid="ticket-detail-panel"
        >
          <Spinner size="large" />
        </Stack>
      )
    }

    if (!ticket) return null

    return (
      <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-detail-panel">
        <ResourceHeader title={ticket.title} metadata={[]} />
        <TicketAiInsights
          aiSummary={ticket.aiSummary}
          aiResolutionProposal={ticket.aiResolutionProposal}
          aiSuggestionsMode={aiEnabled ? ticket.aiSuggestionsMode : 'disabled'}
          aiSuggestionsGeneratedAt={ticket.aiSuggestionsGeneratedAt}
          ticketUpdatedAt={ticket.updatedAt}
          onGenerate={handleGenerateAiSuggestions}
          generating={generatingAiSuggestions}
        />
        <TicketDetailList
          ticket={ticket}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          onAssigneeChange={handleAssigneeChange}
        />
        <TicketAttachments ticket={ticket} onAttachmentAdded={() => refetchTicket()} />
        <TicketComments ticketId={ticket.id} aiEnabled={aiEnabled} />
      </Stack>
    )
  }

  return (
    <F0Dialog
      isOpen={open}
      position="right"
      module={{
        id: 'inbox',
        label: i18n.t('ticketing.sidebar.inbox'),
      }}
      onClose={closeActivity}
      otherActions={[]}
    >
      {renderContent()}
    </F0Dialog>
  )
}
